import { withViewComponent } from '@updater-legacy/components/custom-element';
import '@updater-legacy/components/popup';
import styles from './index.css';

const OFFSET = 8;
const POSITIONS = {
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left',
};

const offset = (position, { popupDimensions, componentDimensions }) => {
  switch (position) {
    case POSITIONS.RIGHT:
      return {
        x: componentDimensions.width + OFFSET,
        y: -componentDimensions.height / 2 - popupDimensions.height / 2,
      };
    case POSITIONS.BOTTOM:
      return {
        x: -popupDimensions.width / 2 + componentDimensions.width / 2,
        y: OFFSET,
      };
    case POSITIONS.LEFT:
      return {
        x: -popupDimensions.width - OFFSET,
        y: -componentDimensions.height / 2 - popupDimensions.height / 2,
      };
    case POSITIONS.TOP:
      return {
        x: -popupDimensions.width / 2 + componentDimensions.width / 2,
        y: -popupDimensions.height - componentDimensions.height - OFFSET,
      };
  }
};

const transform = (position, dimensions) => {
  const { x, y } = offset(position, dimensions);
  return `translate(${x}px, ${y}px)`;
};

const REFLECTED_ATTRIBUTES = [
  'enabled',
  'position',
  'label',
  'trigger',
  'width',
];

export default class Tooltip extends withViewComponent(styles)() {
  static observedAttributes = REFLECTED_ATTRIBUTES;
  static reflectedAttributes = REFLECTED_ATTRIBUTES;
  static attributeTypes = { enabled: 'boolean' };

  _state = {
    open: false,
  };

  connected() {
    this.position = this.position || POSITIONS.TOP;
    this.label = this.label || '';
    this.trigger = this.trigger || 'hover';
  }

  disconnected() {
    this._removeListeners();
  }

  updated() {
    this._render();
    this._removeListeners();
    this._addListeners();
  }

  _addListeners() {
    if (this.enabled) {
      switch (this.trigger) {
        case 'hover':
          this.addEventListener('mouseenter', this._handleOpen);
          this.addEventListener('mouseleave', this._handleClose);
          break;
        case 'click':
          this.addEventListener('click', this._handleClick);
          document.addEventListener('click', this._handleClose);
          break;
        default:
          break;
      }
    }
  }

  _removeListeners() {
    this.removeEventListener('mouseenter', this._handleOpen);
    this.removeEventListener('mouseleave', this._handleClose);
    this.removeEventListener('click', this._handleClick);
    document.removeEventListener('click', this._handleClose);
  }

  _handleOpen = () => {
    this.mergeState({ open: true });
  };

  _handleClick = (event) => {
    // We need to stop propagation here to prevent the document from
    // closig the tooltip at the event bubbles up.
    event.stopPropagation();
    const open = !(this._state.open && this.contains(event.target));
    this.mergeState({ open });
  };

  _handleClose = () => {
    this.mergeState({ open: false });
  };

  _updatePosition = () => {
    const position = this.position;
    const popup = this.shadowRoot.querySelector('upd-popup');

    popup.children[0].style.width = this.width ? `${this.width}px` : undefined;

    const componentDimensions = this.getBoundingClientRect();
    const popupDimensions = popup.children[0].getBoundingClientRect();
    const dimensions = {
      componentDimensions,
      popupDimensions,
    };
    popup.style.transform = transform(position, dimensions);
    popup.setAttribute('open', '');
  };

  _render = () => {
    const {
      position,
      label,
      _state: { open },
    } = this;
    const containerClasses = ['container', `${position}`];

    // prettier-ignore
    this.render`
      <slot></slot>
      ${open ? this.wire()`<upd-popup><div class=${containerClasses.join(' ')}>${label}</div></upd-popup>` : ''}
    `;

    if (open) {
      // When polyfilled the children don't synchronously get setup.
      // This means that we have to wait for `<upd-popup>` to be built, and
      // styled, before we can correctly position the tooltip.
      setTimeout(this._updatePosition);
    }
  };
}

customElements.define('upd-tooltip', Tooltip);
